import { useMemo, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import type { DatumId } from "@nivo/pie";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { abbreviateNumber } from "@/react/helpers/number";
import { CHART_TYPES } from "@/react/hooks/analytics/chartTypes";
import { parseDayIdToLocalDate } from "@/react/utils/dates/dayId";
import { useThemeContext } from "@circle-react/providers";
import { ChartContainer } from "../../shared/ChartContainer";
import { EmptyState } from "../../shared/EmptyState";
import { LegendComponent } from "../../shared/LegendComponent";
import { LoadingChartComponent } from "../../shared/LoadingChartComponent";
import { useContainerSize } from "../../shared/hooks/useContainerSize";
import { useTickValues } from "../../shared/hooks/useTickValues";
import { DATE_FORMAT } from "../constants";
import { createChartGradients } from "../utils/chartGradients";
import { buildRightAxis, buildYScale } from "./axisBuilders";
import { baseAxisProps, lineChartTheme } from "./constants";
import type { LineChartComponentProps } from "./types";
import { formatLineChartData } from "./utils";

export const LineChartComponent = ({
  isLoading,
  className,
  title,
  data,
  tooltip,
  titleLabel,
  indexBy,
  seriesKeys,
  colors,
  curve = "monotoneX",
  enableArea = true,
  enableGridX = false,
  enableGridY = true,
  emptyStateTitle,
  emptyStateDescription,
  customTooltip,
  rootLocale,
  scaleType = "linear",
  showLegend = false,
  secondaryAxis,
  translateLegendLabels = true,
}: LineChartComponentProps) => {
  const { containerWidth, containerRef } = useContainerSize();
  const [activeId, setActiveId] = useState<DatumId | null>(null);

  const activeColors = seriesKeys.map((key, index) =>
    activeId && activeId !== key ? `${colors[index]}80` : colors[index],
  );

  const secondaryKeys = (secondaryAxis && secondaryAxis.keys) || [];

  const seriesColorById: Record<string, string> = seriesKeys.reduce(
    (acc, key, index) => {
      acc[key] = activeColors[index];
      return acc;
    },
    {} as Record<string, string>,
  );

  const { gradientDefs, fillPattern } = useMemo(
    () =>
      createChartGradients({
        type: "area",
        seriesKeys,
        colors,
        activeId,
      }),
    [seriesKeys, colors, activeId],
  );

  const { isLightAppearance } = useThemeContext();

  const tickValues = useTickValues({
    data,
    containerWidth,
    indexBy,
  });

  const formattedPrimaryData = useMemo(
    () => formatLineChartData(data, seriesKeys, indexBy),
    [data, seriesKeys, indexBy],
  );

  const primaryKeysForNormalize = useMemo(
    () =>
      secondaryKeys.length
        ? seriesKeys.filter(k => !secondaryKeys.includes(k))
        : seriesKeys,
    [seriesKeys, secondaryKeys],
  );

  const yMaxForNormalize = useMemo(() => {
    if (!secondaryKeys.length) return null;
    return (
      data.reduce((maxAcc, item) => {
        const rowMax = primaryKeysForNormalize.reduce((acc, key) => {
          const v = Number((item as any)[key] || 0);
          return v > acc ? v : acc;
        }, 0);
        return rowMax > maxAcc ? rowMax : maxAcc;
      }, 0) || 1
    );
  }, [data, primaryKeysForNormalize, secondaryKeys.length]);

  const formattedData = useMemo(() => {
    if (!secondaryKeys.length || !yMaxForNormalize) return formattedPrimaryData;
    return seriesKeys.map(seriesKey => ({
      id: seriesKey,
      data: data.map(item => {
        const x = (item as any)[indexBy];
        const raw = Number((item as any)[seriesKey] || 0);
        const y = secondaryKeys.includes(seriesKey)
          ? (raw / 100) * yMaxForNormalize
          : raw;
        return { x, y };
      }),
    }));
  }, [
    yMaxForNormalize,
    seriesKeys,
    data,
    indexBy,
    secondaryKeys,
    formattedPrimaryData,
  ]);

  const legendData = useMemo(
    () =>
      seriesKeys.map((key, index) => ({
        id: key,
        color: colors[index],
        label: key,
      })),
    [seriesKeys, colors],
  );

  const isSingleDataPoint = useMemo(() => {
    const firstValue = data[0]?.[indexBy];

    return data.every(item => item[indexBy] === firstValue);
  }, [data, indexBy]);

  if (isLoading) {
    return <LoadingChartComponent type="line" className={className} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        chartTitle={title}
        tooltip={tooltip}
        className={className}
        emptyTitle={emptyStateTitle}
        emptyDescription={emptyStateDescription}
      />
    );
  }

  const theme = lineChartTheme(Boolean(isLightAppearance));

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      titleLabel={titleLabel}
      className={className}
      data={data}
      chartType={CHART_TYPES.LINE}
    >
      <div className="flex flex-col gap-4">
        {showLegend && (
          <LegendComponent
            data={legendData}
            orientation="horizontal"
            setActiveId={setActiveId}
            className="self-start"
            rootLocale={translateLegendLabels ? rootLocale : undefined}
          />
        )}
        <div
          ref={containerRef}
          className="relative flex h-80 w-full overflow-visible"
        >
          <ResponsiveLine
            data={formattedData}
            margin={{
              top: 20,
              right: secondaryKeys.length ? 80 : 50,
              bottom: 50,
              left: 50,
            }}
            xScale={{ type: "point" }}
            yScale={buildYScale(
              Boolean(secondaryKeys.length),
              yMaxForNormalize,
              scaleType,
            )}
            curve={curve}
            axisTop={null}
            axisRight={buildRightAxis(
              Boolean(secondaryKeys.length),
              yMaxForNormalize,
              secondaryAxis?.format,
              rootLocale,
            )}
            axisBottom={{
              ...baseAxisProps,
              legendOffset: 40,
              tickValues: tickValues,
              format: (value: string | number | Date) =>
                format(parseDayIdToLocalDate(String(value)), DATE_FORMAT),
            }}
            axisLeft={{
              ...baseAxisProps,
              legendOffset: -45,
              legendPosition: "middle",
              format: abbreviateNumber,
            }}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            gridYValues={5}
            colors={seriesKeys.map(k => seriesColorById[k])}
            enablePoints={isSingleDataPoint}
            pointSize={8}
            pointLabelYOffset={-12}
            enableArea={enableArea}
            defs={gradientDefs}
            fill={fillPattern}
            useMesh
            onMouseEnter={datum => {
              if ("seriesId" in datum) {
                setActiveId(datum.seriesId as DatumId);
              }
            }}
            onMouseLeave={() => setActiveId(null)}
            tooltip={customTooltip}
            theme={theme}
            ariaLabel={t("settings.analytics.line_chart")}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

LineChartComponent.displayName = "LineChartComponent";
